import {
  useV2CreateClientLocationForClientMutation,
  useV2UpdateClientLocationMutation,
  useV2DeleteClientLocationMutation,
} from "@generated";
import type { V2ClientLocationResource } from "@generated";
import type { LocationFormValues } from "../types";

// The `client` relationship is inferred from the URL clientId server-side, so
// we build the resource and cast (the generated type marks relationships
// required and doesn't allow the nulls sent on update).
// Blank optional fields are omitted on create, but sent as null on update so a
// user can clear a previously saved value.
export const buildLocationData = (
  values: LocationFormValues,
  id?: string
): V2ClientLocationResource => {
  const blank = id ? null : undefined;
  return {
    type: "client-locations",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      street: values.street || blank,
      street2: values.street2 || blank,
      city: values.city || blank,
      state: values.state || blank,
      "postal-code": values.postalCode || blank,
      country: values.country || blank,
    },
  } as unknown as V2ClientLocationResource;
};

export const useLocationMutations = (clientId: string) => {
  const [create] = useV2CreateClientLocationForClientMutation();
  const [update] = useV2UpdateClientLocationMutation();
  const [destroy] = useV2DeleteClientLocationMutation();

  return {
    create: (values: LocationFormValues) =>
      create({
        clientId: clientId,
        body: { data: buildLocationData(values) },
      }).unwrap(),
    update: (id: string, values: LocationFormValues) =>
      update({
        clientId: clientId,
        id: id,
        body: { data: buildLocationData(values, id) },
      }).unwrap(),
    archive: (id: string) =>
      destroy({ clientId: clientId, id: id }).unwrap(),
  };
};

export default useLocationMutations;
